import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { createContext, useState, useRef, useEffect, useCallback } from "react";
import { useJsApiLoader, GoogleMap } from "@react-google-maps/api";
import axios from "axios";
import debounce from "lodash/debounce";
import BrandclubStoreConfigUtils from "../../../utils/brandclubStoreConfigUtils.js";
import { getEntityImage } from "../../../utils/assetUtils.js";
import { useGeolocation } from "./useGeolocation.js";
import { StoreLocatorWidget as StoreLocatorWidget$1 } from "@brandclub/bc-widgets";
const StoreLocatorStyleContext = createContext(null);
const libraries = ["places", "marker", "geocoding"];
const hasSignificantPositionChange = (oldLat, oldLng, newLat, newLng) => {
  const roundToFour = (num) => Math.round(num * 1e4) / 1e4;
  return roundToFour(oldLat) !== roundToFour(newLat) || roundToFour(oldLng) !== roundToFour(newLng);
};
const ScrollableCarousel = ({ children, scrollRef, paddingX }) => {
  const internalRef = useRef(null);
  const containerRef = scrollRef || internalRef;
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const checkScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, [containerRef]);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", checkScroll);
      observer.disconnect();
    };
  }, [containerRef, checkScroll]);
  const scroll = (direction) => {
    const el = containerRef.current;
    if (!el) return;
    const amount = 360;
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };
  const arrowStyle = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#FFFFFF",
    border: "1px solid #DCDDDC",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 2,
    padding: 0,
    transition: "opacity 0.2s"
  };
  return /* @__PURE__ */ jsxs("div", { className: "tw:relative", onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        ref: containerRef,
        className: "tw:flex tw:gap-3 tw:overflow-x-auto tw:scrollbar-hide tw:snap-x tw:snap-mandatory tw:touch-pan-x",
        style: {
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          ...paddingX && { paddingLeft: paddingX, paddingRight: paddingX, scrollPaddingLeft: paddingX }
        },
        children
      }
    ),
    isHovered && canScrollLeft && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => scroll("left"),
        style: { ...arrowStyle, left: paddingX || "-4px" },
        "aria-label": "Scroll left",
        children: /* @__PURE__ */ jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx("path", { d: "M10 12L6 8L10 4", stroke: "#333", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) })
      }
    ),
    isHovered && canScrollRight && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => scroll("right"),
        style: { ...arrowStyle, right: paddingX || "-4px" },
        "aria-label": "Scroll right",
        children: /* @__PURE__ */ jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", children: /* @__PURE__ */ jsx("path", { d: "M6 4L10 8L6 12", stroke: "#333", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) })
      }
    )
  ] });
};
const SearchIcon = ({ className }) => /* @__PURE__ */ jsxs("svg", { className, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [
  /* @__PURE__ */ jsx("circle", { cx: "6.5", cy: "6.5", r: "5", stroke: "currentColor", strokeWidth: "1.5" }),
  /* @__PURE__ */ jsx("path", { d: "M10.5 10.5L14.5 14.5", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round" })
] });
const getCSSVariable = (varName, fallback = "#000") => {
  if (typeof window === "undefined") return fallback;
  const root = document.documentElement;
  const value = getComputedStyle(root).getPropertyValue(varName).trim();
  return value || fallback;
};
const getColorWithOpacity = (varName, opacity, fallback = "#000") => {
  const color = getCSSVariable(varName, fallback);
  if (color.startsWith("oklch")) {
    const match = color.match(/oklch\(([^/)]+)(?:\s*\/\s*[\d.]+)?\)/);
    if (match) {
      return `oklch(${match[1].trim()} / ${opacity})`;
    }
  }
  if (color.startsWith("#")) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgbMatch) {
    return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, ${opacity})`;
  }
  if (fallback.startsWith("#")) {
    const r = parseInt(fallback.slice(1, 3), 16);
    const g = parseInt(fallback.slice(3, 5), 16);
    const b = parseInt(fallback.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return fallback;
};
const CloseIcon = ({ className, onClick }) => {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      className,
      onClick,
      width: "14",
      height: "14",
      viewBox: "0 0 14 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: { cursor: "pointer" },
      children: /* @__PURE__ */ jsx("path", { d: "M2 2L12 12M12 2L2 12", stroke: "white", strokeWidth: "2", strokeLinecap: "round" })
    }
  );
};
const RetailerSummaryCard = ({ retailerSummary, onClick }) => {
  const nearestStore = retailerSummary.stores[0];
  const nearestDistance = nearestStore == null ? void 0 : nearestStore.distance;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      onClick,
      className: "tw:flex-shrink-0 tw:flex tw:cursor-pointer tw:transition-all hover:tw:shadow-md tw:snap-start tw:touch-manipulation tw:overflow-hidden",
      style: {
        fontFamily: "Poppins, sans-serif",
        width: "min(calc(100vw - 80px), 349px)",
        height: "88px",
        padding: "12px 16px",
        gap: "16px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #DCDDDC",
        borderRadius: "8px",
        boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.08, "#000")}`
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA] tw:flex tw:items-center tw:justify-center tw:self-start",
            style: { width: "33px", height: "33px" },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                src: getEntityImage(retailerSummary.retailerId, "retailer"),
                alt: retailerSummary.retailerName,
                className: "tw:w-full tw:h-full tw:object-contain"
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:min-w-0 tw:overflow-hidden tw:flex tw:flex-col tw:justify-between tw:self-stretch", children: [
          /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-start tw:justify-between", style: { gap: "8px" }, children: [
            /* @__PURE__ */ jsxs("div", { className: "tw:min-w-0 tw:overflow-hidden tw:flex tw:flex-col", style: { gap: "4px" }, children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: "tw:font-medium tw:text-primary tw:truncate tw:block",
                  style: { fontSize: "12px", lineHeight: "16px", letterSpacing: "0.08px" },
                  children: retailerSummary.retailerName
                }
              ),
              /* @__PURE__ */ jsxs(
                "span",
                {
                  className: "tw:font-semibold tw:text-primary tw:truncate tw:block",
                  style: { fontSize: "16px", lineHeight: "24px", letterSpacing: "0.08px" },
                  children: [
                    retailerSummary.storeCount,
                    " store",
                    retailerSummary.storeCount !== 1 ? "s" : ""
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: (e) => {
                  e.stopPropagation();
                  onClick();
                },
                className: "tw:h-[32px] tw:min-h-[32px] tw:px-[16px] tw:py-[4px] tw:bg-[#F6F8FA] tw:rounded-[16px] tw:border-0 tw:cursor-pointer tw:flex-shrink-0 tw:flex tw:items-center tw:justify-center tw:touch-manipulation",
                children: /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "tw:font-semibold tw:text-primary tw:whitespace-nowrap",
                    style: { fontSize: "12px", lineHeight: "24px", letterSpacing: "0.08px" },
                    children: "View stores"
                  }
                )
              }
            )
          ] }),
          nearestDistance != null && typeof nearestDistance === "number" && nearestDistance > 0 && /* @__PURE__ */ jsxs(
            "span",
            {
              className: "tw:font-medium tw:text-primary tw:truncate tw:block",
              style: { fontSize: "14px", lineHeight: "16px", letterSpacing: "0.08px" },
              children: [
                "Nearest ",
                nearestDistance.toFixed(1),
                " miles away"
              ]
            }
          )
        ] })
      ]
    }
  );
};
const StoreCard = ({ store, onDetailsClick }) => {
  var _a;
  const fullAddress = `${store.address}, ${store.city}, ${store.state} ${store.zipcode}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:flex-shrink-0 tw:flex tw:cursor-pointer tw:transition-all hover:tw:shadow-md tw:snap-start tw:touch-manipulation tw:overflow-hidden",
      style: {
        fontFamily: "Poppins, sans-serif",
        width: "min(calc(100vw - 80px), 349px)",
        height: "88px",
        padding: "12px 16px",
        gap: "16px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #DCDDDC",
        borderRadius: "8px",
        boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.08, "#000")}`
      },
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA] tw:self-start",
            style: { width: "33px", height: "33px" },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                src: getEntityImage(store.retailerid, "retailer"),
                alt: store.storename ?? void 0,
                className: "tw:w-full tw:h-full tw:object-contain"
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:min-w-0 tw:overflow-hidden tw:flex tw:flex-col tw:justify-between tw:self-stretch", children: [
          /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-start tw:justify-between", style: { gap: "8px" }, children: [
            /* @__PURE__ */ jsxs("div", { className: "tw:min-w-0 tw:overflow-hidden tw:flex tw:flex-col", style: { gap: "4px" }, children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: "tw:font-medium tw:text-primary tw:truncate tw:block",
                  style: { fontSize: "12px", lineHeight: "16px", letterSpacing: "0.08px" },
                  children: ((_a = store.storename) == null ? void 0 : _a.split(" ")[0]) || "Store"
                }
              ),
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: "tw:font-semibold tw:text-primary tw:truncate tw:block",
                  style: { fontSize: "16px", lineHeight: "24px", letterSpacing: "0.08px" },
                  children: store.storename || store.address
                }
              )
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                onClick: (e) => {
                  e.stopPropagation();
                  onDetailsClick();
                },
                className: "tw:h-[32px] tw:min-h-[32px] tw:px-[16px] tw:py-[4px] tw:bg-[#F6F8FA] tw:rounded-[16px] tw:border-0 tw:cursor-pointer tw:flex-shrink-0 tw:flex tw:items-center tw:justify-center tw:touch-manipulation",
                children: /* @__PURE__ */ jsx(
                  "span",
                  {
                    className: "tw:font-semibold tw:text-primary tw:whitespace-nowrap",
                    style: { fontSize: "12px", lineHeight: "24px", letterSpacing: "0.08px" },
                    children: "Details"
                  }
                )
              }
            )
          ] }),
          /* @__PURE__ */ jsx(
            "span",
            {
              className: "tw:font-medium tw:text-primary tw:truncate tw:block",
              style: { fontSize: "14px", lineHeight: "16px", letterSpacing: "0.08px" },
              children: fullAddress
            }
          )
        ] })
      ]
    }
  );
};
const StoreDetailPanel = ({ store, details, loading, onClose }) => {
  var _a, _b;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:absolute tw:bg-white tw:shadow-lg tw:overflow-hidden tw:flex tw:flex-col tw:z-10 tw:inset-0 sm:tw:inset-auto sm:tw:w-[300px] sm:tw:right-3 sm:tw:top-3 sm:tw:bottom-3 sm:tw:rounded-[10px]",
      style: { fontFamily: "Poppins, sans-serif" },
      children: [
        /* @__PURE__ */ jsxs("div", { className: "tw:relative tw:h-[164px] tw:flex-shrink-0 tw:bg-[#F6F8FA]", children: [
          /* @__PURE__ */ jsx(
            "img",
            {
              src: `https://maps.googleapis.com/maps/api/streetview?size=300x164&location=${(_a = store.location) == null ? void 0 : _a.lat},${(_b = store.location) == null ? void 0 : _b.lon}&key=${BrandclubStoreConfigUtils.getGoogleMapsApiKey()}`,
              alt: store.storename || "Store",
              className: "tw:w-full tw:h-full tw:object-cover sm:tw:rounded-t-[10px]",
              onError: (e) => {
                e.currentTarget.style.display = "none";
              }
            }
          ),
          /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:top-3 tw:right-3", children: /* @__PURE__ */ jsx(
            "div",
            {
              className: "tw:w-8 tw:h-8 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:cursor-pointer",
              style: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
              onClick: onClose,
              children: /* @__PURE__ */ jsx(CloseIcon, {})
            }
          ) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:overflow-auto tw:p-4", children: [
          /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-start tw:gap-2 tw:mb-2", children: [
            /* @__PURE__ */ jsx("div", { className: "tw:w-8 tw:h-8 tw:rounded-full tw:overflow-hidden tw:flex-shrink-0 tw:bg-[#F6F8FA]", children: /* @__PURE__ */ jsx(
              "img",
              {
                src: getEntityImage(store.retailerid, "retailer"),
                alt: store.storename ?? void 0,
                className: "tw:w-full tw:h-full tw:object-contain"
              }
            ) }),
            /* @__PURE__ */ jsx("h3", { className: "tw:text-lg tw:font-semibold tw:text-primary tw:m-0 tw:leading-[24px] tw:tracking-[0.08px]", children: store.storename || "Store" })
          ] }),
          loading ? /* @__PURE__ */ jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-1/2 tw:mb-4" }) : (details == null ? void 0 : details.openNow) !== void 0 ? /* @__PURE__ */ jsxs("p", { className: "tw:text-xs tw:m-0 tw:leading-[16px] tw:tracking-[0.08px] tw:mb-4", children: [
            /* @__PURE__ */ jsx("span", { className: `tw:font-bold ${details.openNow ? "tw:text-[#71c184]" : "tw:text-red-500"}`, children: details.openNow ? "Open" : "Closed" }),
            details.openUntil && details.openNow && /* @__PURE__ */ jsxs("span", { className: "tw:text-primary tw:font-medium", children: [
              " until ",
              details.openUntil,
              " today"
            ] })
          ] }) : null,
          /* @__PURE__ */ jsxs("div", { className: "tw:space-y-3", children: [
            /* @__PURE__ */ jsx("p", { className: "tw:text-xs tw:font-bold tw:text-primary tw:m-0 tw:leading-[16px] tw:tracking-[0.08px]", children: "Store information" }),
            /* @__PURE__ */ jsxs("p", { className: "tw:text-xs tw:font-medium tw:text-primary tw:m-0 tw:leading-[16px] tw:tracking-[0.08px]", children: [
              store.storename,
              /* @__PURE__ */ jsx("br", {}),
              store.address,
              ", ",
              store.city,
              ", ",
              store.state,
              " ",
              store.zipcode
            ] }),
            loading ? /* @__PURE__ */ jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-1/3" }) : (details == null ? void 0 : details.phone) ? /* @__PURE__ */ jsx(
              "a",
              {
                href: `tel:${details.phone}`,
                className: "tw:text-xs tw:font-medium tw:text-primary tw:no-underline hover:tw:underline tw:block tw:leading-[16px] tw:tracking-[0.08px]",
                children: details.phone
              }
            ) : null,
            loading ? /* @__PURE__ */ jsx("div", { className: "tw:space-y-2", children: [1, 2, 3].map((i) => /* @__PURE__ */ jsx("div", { className: "tw:h-4 tw:bg-[#F6F8FA] tw:rounded tw:animate-pulse tw:w-full" }, i)) }) : (details == null ? void 0 : details.regularHours) && details.regularHours.length > 0 ? /* @__PURE__ */ jsx("div", { children: details.regularHours.map((h, i) => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:justify-between tw:text-xs tw:font-medium tw:text-primary", children: [
              /* @__PURE__ */ jsx("span", { className: "tw:leading-[16px] tw:tracking-[0.08px]", children: h.day }),
              /* @__PURE__ */ jsx("span", { className: "tw:leading-[16px] tw:tracking-[0.08px]", children: h.hours })
            ] }, i)) }) : null,
            !loading && (details == null ? void 0 : details.holidayHours) && details.holidayHours.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx("p", { className: "tw:text-xs tw:font-bold tw:text-primary tw:m-0 tw:mt-4 tw:leading-[16px] tw:tracking-[0.08px]", children: "Holiday hours" }),
              /* @__PURE__ */ jsx("div", { children: details.holidayHours.map((h, i) => /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:justify-between tw:text-xs tw:font-medium tw:text-primary", children: [
                /* @__PURE__ */ jsx("span", { className: "tw:leading-[16px] tw:tracking-[0.08px]", children: h.date }),
                /* @__PURE__ */ jsx("span", { className: "tw:leading-[16px] tw:tracking-[0.08px]", children: h.hours })
              ] }, i)) })
            ] })
          ] })
        ] })
      ]
    }
  );
};
const StoreLocatorWidgetInner = ({
  stacklineSkus,
  apiUrl,
  widgetState
}) => {
  const [viewState, setViewState] = useState("retailer-summary");
  const [stores, setStores] = useState([]);
  const [retailerSummaries, setRetailerSummaries] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [isMarkerLibraryLoaded, setIsMarkerLibraryLoaded] = useState(false);
  const markersRef = useRef([]);
  const fetchAbortControllerRef = useRef(null);
  const { location, getLocation, hasCheckedPermission } = useGeolocation();
  const [initialCenter, setInitialCenter] = useState(null);
  const [lastFetchedPosition, setLastFetchedPosition] = useState(null);
  const carouselRef = useRef(null);
  const [isOnlineOnly, setIsOnlineOnly] = useState(false);
  const [hasAnyStores, setHasAnyStores] = useState(true);
  const [_totalStoresGlobally, setTotalStoresGlobally] = useState(0);
  const [hasUserPanned, setHasUserPanned] = useState(false);
  const [hasAppliedBrowserLocation, setHasAppliedBrowserLocation] = useState(false);
  const hasFetchedInitialStores = useRef(false);
  const [searchInput, setSearchInput] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const searchWrapperRef = useRef(null);
  const googleMapsApiKey = BrandclubStoreConfigUtils.getGoogleMapsApiKey();
  const googleMapsMapId = BrandclubStoreConfigUtils.getGoogleMapsMapId();
  const API_URL = apiUrl || BrandclubStoreConfigUtils.getApiHostName();
  const { isLoaded, loadError } = useJsApiLoader({
    id: "bc-concierge-store-locator",
    googleMapsApiKey,
    libraries
  });
  useEffect(() => {
    var _a, _b;
    if (isLoaded && ((_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places)) {
      const token = new window.google.maps.places.AutocompleteSessionToken();
      setSessionToken(token);
    }
  }, [isLoaded]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    getLocation();
  }, []);
  useEffect(() => {
    if (location && // Browser location is available
    !hasUserPanned && // User hasn't manually moved the map
    !hasAppliedBrowserLocation && // We haven't already applied browser location
    map) {
      map.panTo(location);
      map.setZoom(11);
      setInitialCenter(location);
      setHasAppliedBrowserLocation(true);
      fetchStores({
        userLat: location.lat,
        userLon: location.lng,
        radiusMiles: 50
      });
    }
  }, [location, hasUserPanned, hasAppliedBrowserLocation, map]);
  const fetchStores = useCallback(
    async (params) => {
      var _a, _b, _c, _d, _e;
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
      const controller = new AbortController();
      fetchAbortControllerRef.current = controller;
      try {
        setLoading(true);
        const response = await axios.post(
          `${API_URL}/store-locator/search`,
          {
            stacklineSkus,
            userLat: params.userLat,
            userLon: params.userLon,
            radiusMiles: params.radiusMiles
          },
          { signal: controller.signal }
        );
        const storeData = ((_a = response.data) == null ? void 0 : _a.stores) || [];
        setStores(storeData);
        setIsOnlineOnly(((_b = response.data) == null ? void 0 : _b.isOnlineOnly) || false);
        setHasAnyStores(((_c = response.data) == null ? void 0 : _c.hasAnyStores) !== false);
        setTotalStoresGlobally(((_d = response.data) == null ? void 0 : _d.totalStoresGlobally) || 0);
        if (params.isInitialFetch && ((_e = response.data) == null ? void 0 : _e.detectedLocation) && !initialCenter) {
          const detected = response.data.detectedLocation;
          setInitialCenter({ lat: detected.lat, lng: detected.lng });
        }
        const retailerMap = /* @__PURE__ */ new Map();
        storeData.forEach((store) => {
          var _a2;
          if (!retailerMap.has(store.retailerid)) {
            retailerMap.set(store.retailerid, {
              retailerId: store.retailerid,
              retailerName: ((_a2 = store.storename) == null ? void 0 : _a2.split(" ")[0]) || `Retailer ${store.retailerid}`,
              storeCount: 0,
              stores: []
            });
          }
          const summary = retailerMap.get(store.retailerid);
          if (summary) {
            summary.storeCount++;
            summary.stores.push(store);
          }
        });
        setRetailerSummaries(Array.from(retailerMap.values()));
      } catch (error) {
        if (axios.isCancel(error)) return;
        console.error("Error fetching stores:", error);
        setStores([]);
        setRetailerSummaries([]);
        setIsOnlineOnly(false);
        setHasAnyStores(true);
        setTotalStoresGlobally(0);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [API_URL, stacklineSkus, initialCenter]
  );
  const fetchStoreDetails = async (store) => {
    var _a, _b, _c;
    try {
      setDetailsLoading(true);
      const response = await axios.post(`${API_URL}/store-locator/details`, {
        storeName: store.storename,
        address: store.address,
        city: store.city,
        state: store.state,
        zipcode: store.zipcode,
        lat: (_a = store.location) == null ? void 0 : _a.lat,
        lon: (_b = store.location) == null ? void 0 : _b.lon
      });
      if ((_c = response.data) == null ? void 0 : _c.success) {
        setStoreDetails(response.data.details);
      } else {
        setStoreDetails(null);
      }
    } catch (error) {
      console.error("Error fetching store details:", error);
      setStoreDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };
  useEffect(() => {
    if (hasFetchedInitialStores.current) return;
    if (location) {
      hasFetchedInitialStores.current = true;
      setInitialCenter(location);
      fetchStores({
        userLat: location.lat,
        userLon: location.lng,
        radiusMiles: 50,
        isInitialFetch: true
      });
    } else if (hasCheckedPermission) {
      hasFetchedInitialStores.current = true;
      fetchStores({
        radiusMiles: 50,
        isInitialFetch: true
      });
    }
  }, [location, hasCheckedPermission, fetchStores]);
  const debouncedFetchStores = useCallback(
    debounce((params) => {
      fetchStores(params);
    }, 500),
    [fetchStores]
  );
  const handleMapIdle = useCallback(() => {
    if (!map) return;
    const center = map.getCenter();
    if (!center) return;
    const newLat = center.lat();
    const newLng = center.lng();
    if (!lastFetchedPosition || hasSignificantPositionChange(lastFetchedPosition.lat, lastFetchedPosition.lng, newLat, newLng)) {
      if (lastFetchedPosition) {
        setHasUserPanned(true);
      }
      setLastFetchedPosition({ lat: newLat, lng: newLng });
      debouncedFetchStores({
        userLat: newLat,
        userLon: newLng,
        radiusMiles: 50
      });
    }
  }, [map, lastFetchedPosition, debouncedFetchStores]);
  const createMarkerElement = (store, isSelected) => {
    const markerDiv = document.createElement("div");
    markerDiv.style.position = "relative";
    markerDiv.style.cursor = "pointer";
    markerDiv.style.transform = isSelected ? "scale(1.2)" : "scale(1)";
    markerDiv.style.transition = "transform 0.2s ease";
    markerDiv.style.zIndex = isSelected ? "10" : "1";
    const pinColor = getCSSVariable("--color-primary", "#031425");
    const pinWrapper = document.createElement("div");
    pinWrapper.style.position = "relative";
    pinWrapper.style.width = "28px";
    pinWrapper.style.height = "31.81px";
    const shadowColor = getColorWithOpacity("--color-foreground", isSelected ? 0.35 : 0.2, "#000");
    pinWrapper.style.filter = `drop-shadow(0 2px 4px ${shadowColor})`;
    pinWrapper.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32" fill="none">
        <path d="M14 0C21.732 0 28 6.26801 28 14C28 20.8082 23.14 26.4799 16.7002 27.7383L14.8896 31.2666C14.5178 31.9909 13.4822 31.9909 13.1104 31.2666L11.2988 27.7383C4.85951 26.4795 0 20.8079 0 14C0 6.26801 6.26801 0 14 0Z" fill="${pinColor}"/>
        <circle cx="14" cy="14" r="12" fill="white"/>
      </svg>
    `;
    const logoContainer = document.createElement("div");
    logoContainer.style.position = "absolute";
    logoContainer.style.top = "2px";
    logoContainer.style.left = "2px";
    logoContainer.style.width = "24px";
    logoContainer.style.height = "24px";
    logoContainer.style.borderRadius = "50%";
    logoContainer.style.overflow = "hidden";
    logoContainer.style.display = "flex";
    logoContainer.style.alignItems = "center";
    logoContainer.style.justifyContent = "center";
    logoContainer.style.backgroundColor = "#FFFFFF";
    const logo = document.createElement("img");
    logo.src = getEntityImage(store.retailerid, "retailer");
    logo.alt = store.storename || "";
    logo.style.width = "24px";
    logo.style.height = "24px";
    logo.style.objectFit = "contain";
    logoContainer.appendChild(logo);
    pinWrapper.appendChild(logoContainer);
    markerDiv.appendChild(pinWrapper);
    return markerDiv;
  };
  useEffect(() => {
    var _a, _b, _c;
    if (!map || !isMarkerLibraryLoaded || !((_c = (_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.marker) == null ? void 0 : _c.AdvancedMarkerElement)) return;
    markersRef.current.forEach((marker) => {
      marker.map = null;
    });
    markersRef.current = [];
    const displayStores = viewState === "retailer-summary" ? stores : viewState === "store-details" ? stores : selectedRetailerId ? stores.filter((s) => s.retailerid === selectedRetailerId) : stores;
    displayStores.forEach((store) => {
      var _a2, _b2;
      if (!((_a2 = store.location) == null ? void 0 : _a2.lat) || !((_b2 = store.location) == null ? void 0 : _b2.lon)) return;
      const isSelected = selectedStore && selectedStore.address === store.address;
      const markerDiv = createMarkerElement(store, !!isSelected);
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map,
        position: { lat: store.location.lat, lng: store.location.lon },
        content: markerDiv,
        title: store.storename || void 0
      });
      marker.addListener("click", () => {
        setSelectedStore(store);
        if (viewState === "retailer-summary") {
          setSelectedRetailerId(store.retailerid);
          setViewState("store-list");
        } else if (viewState === "store-list") {
          setViewState("store-details");
          fetchStoreDetails(store);
        }
      });
      markersRef.current.push(marker);
    });
  }, [map, stores, selectedStore, isMarkerLibraryLoaded, viewState, selectedRetailerId]);
  const handleMapLoad = (loadedMap) => {
    setMap(loadedMap);
    setIsMarkerLibraryLoaded(false);
    setTimeout(() => {
      setIsMarkerLibraryLoaded(true);
    }, 0);
  };
  const handleRetailerSelect = (retailerId) => {
    setSelectedRetailerId(retailerId);
    setViewState("store-list");
    const firstStore = stores.find((s) => s.retailerid === retailerId);
    if (firstStore && map && firstStore.location) {
      map.panTo({ lat: firstStore.location.lat, lng: firstStore.location.lon });
      map.setZoom(12);
    }
  };
  const handleStoreDetailsClick = (store) => {
    setSelectedStore(store);
    setViewState("store-details");
    fetchStoreDetails(store);
    if (map && store.location) {
      map.panTo({ lat: store.location.lat, lng: store.location.lon });
      map.setZoom(15);
    }
  };
  const handleCloseDetails = () => {
    setViewState(selectedRetailerId ? "store-list" : "retailer-summary");
    setSelectedStore(null);
    setStoreDetails(null);
  };
  const handleBackToAllRetailers = () => {
    setSelectedRetailerId(null);
    setViewState("retailer-summary");
    setSelectedStore(null);
    setStoreDetails(null);
  };
  const handleSearchInputChange = (event) => {
    var _a, _b;
    const value = event.target.value;
    setSearchInput(value);
    if (value && value.length >= 2 && sessionToken && ((_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places)) {
      const useNewAPI = typeof window.google.maps.places.AutocompleteSuggestion !== "undefined" && typeof window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions === "function";
      if (useNewAPI) {
        const request = {
          input: value,
          sessionToken,
          includedRegionCodes: ["us"]
        };
        window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request).then((response) => {
          var _a2;
          if ((response == null ? void 0 : response.suggestions) && response.suggestions.length > 0) {
            const predictions2 = [];
            for (const suggestion of response.suggestions) {
              if (suggestion.placePrediction) {
                const placePrediction = suggestion.placePrediction;
                const mainText = ((_a2 = placePrediction.text) == null ? void 0 : _a2.text) || "";
                const terms = mainText.split(", ");
                let offset = 0;
                predictions2.push({
                  place_id: placePrediction.placeId,
                  description: mainText,
                  structured_formatting: {
                    main_text: mainText,
                    secondary_text: "",
                    main_text_matched_substrings: []
                  },
                  terms: terms.map((term) => {
                    const termObj = { value: term, offset };
                    offset += term.length + 2;
                    return termObj;
                  }),
                  types: [],
                  matched_substrings: []
                });
              }
            }
            setPredictions(predictions2);
            setShowPredictions(true);
          } else {
            setPredictions([]);
            setShowPredictions(false);
          }
        }).catch(() => {
          setPredictions([]);
          setShowPredictions(false);
        });
      } else {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          {
            input: value,
            sessionToken,
            // Use componentRestrictions to limit to US cities/regions
            componentRestrictions: { country: "us" }
          },
          (predictions2, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions2) {
              setPredictions(predictions2);
              setShowPredictions(true);
            } else {
              setPredictions([]);
              setShowPredictions(false);
            }
          }
        );
      }
    } else {
      setPredictions([]);
      setShowPredictions(false);
    }
  };
  const handleSelectPrediction = async (prediction) => {
    var _a, _b, _c, _d, _e;
    setSearchInput(prediction.description);
    setPredictions([]);
    setShowPredictions(false);
    const useNewPlaceAPI = typeof ((_c = (_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places) == null ? void 0 : _c.Place) !== "undefined" && typeof window.google.maps.places.Place.prototype.fetchFields === "function";
    if (useNewPlaceAPI && ((_e = (_d = window.google) == null ? void 0 : _d.maps) == null ? void 0 : _e.places)) {
      try {
        const place = new window.google.maps.places.Place({ id: prediction.place_id });
        await place.fetchFields({ fields: ["location"] });
        if (place.location && map) {
          const lat = place.location.lat();
          const lng = place.location.lng();
          map.panTo({ lat, lng });
          map.setZoom(11);
          fetchStores({
            userLat: lat,
            userLon: lng,
            radiusMiles: 50
          });
          const newToken = new window.google.maps.places.AutocompleteSessionToken();
          setSessionToken(newToken);
        }
      } catch (error) {
        console.error("Error fetching place details with new API:", error);
        fetchPlaceDetailsLegacy(prediction.place_id);
      }
    } else {
      fetchPlaceDetailsLegacy(prediction.place_id);
    }
  };
  const fetchPlaceDetailsLegacy = (placeId) => {
    var _a, _b;
    if (!((_b = (_a = window.google) == null ? void 0 : _a.maps) == null ? void 0 : _b.places) || !map) return;
    const serviceDiv = document.createElement("div");
    const placesService = new window.google.maps.places.PlacesService(serviceDiv);
    placesService.getDetails(
      {
        placeId,
        fields: ["geometry"]
      },
      (place, status) => {
        var _a2, _b2, _c;
        if (status === window.google.maps.places.PlacesServiceStatus.OK && ((_a2 = place == null ? void 0 : place.geometry) == null ? void 0 : _a2.location) && map) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          map.panTo({ lat, lng });
          map.setZoom(11);
          fetchStores({
            userLat: lat,
            userLon: lng,
            radiusMiles: 50
          });
          if ((_c = (_b2 = window.google) == null ? void 0 : _b2.maps) == null ? void 0 : _c.places) {
            const newToken = new window.google.maps.places.AutocompleteSessionToken();
            setSessionToken(newToken);
          }
        }
      }
    );
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && predictions.length > 0) {
      handleSelectPrediction(predictions[0]);
    }
  };
  if (loadError) {
    console.error("Google Maps load error:", loadError);
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "tw:w-full tw:h-[400px] tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA] tw:rounded-lg tw:border tw:border-[#DCDDDC]",
        style: { fontFamily: "Poppins, sans-serif" },
        children: /* @__PURE__ */ jsx("p", { className: "tw:text-sm tw:text-red-500", children: "Unable to load store locator. Please try again later." })
      }
    );
  }
  const filteredStores = selectedRetailerId ? stores.filter((s) => s.retailerid === selectedRetailerId) : stores;
  const SHOW_DEBUG = false;
  typeof window !== "undefined" ? `${window.location.origin}/product/p/${stacklineSkus[0]}/product` : "";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "tw:w-full tw:overflow-hidden tw:bg-white tw:my-3 tw:relative tw:mb-6",
      id: "store-locator-widget",
      style: { fontFamily: "Poppins, sans-serif" },
      children: [
        SHOW_DEBUG,
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: "tw:relative tw:w-full tw:overflow-hidden",
            style: { height: "min(636px, 80vh)", borderRadius: "5px" },
            children: [
              !loading && isOnlineOnly ? /* @__PURE__ */ jsx("div", { className: "tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA] tw:rounded-lg tw:border tw:border-[#DCDDDC]", children: /* @__PURE__ */ jsxs("div", { className: "tw:text-center tw:px-4", children: [
                /* @__PURE__ */ jsx("p", { className: "tw:text-sm tw:font-medium tw:text-primary tw:mb-2", children: "This product is only available online" }),
                /* @__PURE__ */ jsx("p", { className: "tw:text-xs tw:text-primary/70", children: "This product is not sold in brick-and-mortar stores. Check the product card above for online purchase options." })
              ] }) }) : (
                /* Google Map - fills the entire container, always shown when product has stores */
                /* @__PURE__ */ jsxs(Fragment, { children: [
                  isLoaded ? /* @__PURE__ */ jsx(
                    GoogleMap,
                    {
                      mapContainerStyle: { width: "100%", height: "100%" },
                      center: initialCenter || { lat: 39.8283, lng: -98.5795 },
                      zoom: location ? 11 : 4,
                      onLoad: handleMapLoad,
                      onIdle: handleMapIdle,
                      options: {
                        mapId: googleMapsMapId,
                        disableDefaultUI: true,
                        zoomControl: true,
                        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP }
                      }
                    }
                  ) : /* @__PURE__ */ jsx("div", { className: "tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-[#F6F8FA]", children: /* @__PURE__ */ jsx("div", { className: "tw:animate-spin tw:w-8 tw:h-8 tw:border-2 tw:border-primary tw:border-t-transparent tw:rounded-full" }) }),
                  !loading && hasAnyStores && stores.length === 0 && /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: "tw:absolute tw:top-1/2 tw:left-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:z-10 tw:pointer-events-none",
                      style: { fontFamily: "Poppins, sans-serif" },
                      children: /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "tw:bg-white/95 tw:backdrop-blur-sm tw:rounded-lg tw:px-4 tw:py-3 tw:text-center",
                          style: {
                            boxShadow: `0 2px 8px ${getColorWithOpacity("--color-foreground", 0.15, "#000")}`,
                            maxWidth: "280px"
                          },
                          children: [
                            /* @__PURE__ */ jsx("p", { className: "tw:text-sm tw:font-medium tw:text-primary tw:mb-1", children: "No stores found in this area" }),
                            /* @__PURE__ */ jsx("p", { className: "tw:text-xs tw:text-primary/70", children: "Try zooming out or searching a different location" })
                          ]
                        }
                      )
                    }
                  )
                ] })
              ),
              isLoaded && !isOnlineOnly && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("div", { className: "tw:absolute tw:z-10", style: { top: "12px", left: "12px" }, ref: searchWrapperRef, children: /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "tw:relative",
                    style: {
                      fontFamily: "Poppins, sans-serif"
                    },
                    children: [
                      /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "tw:flex tw:items-center tw:gap-[12px]",
                          style: {
                            backgroundColor: "#FFFFFF",
                            border: `1px solid ${getCSSVariable("--color-border", "#DCDDDC")}`,
                            borderRadius: "100px",
                            height: "44px",
                            width: "min(calc(100vw - 48px), 352px)",
                            paddingLeft: "16px",
                            paddingRight: "16px",
                            boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.1, "#000")}`
                          },
                          children: [
                            /* @__PURE__ */ jsx(
                              "div",
                              {
                                className: "tw:flex tw:items-center tw:justify-center tw:flex-shrink-0",
                                style: { width: "16px", height: "16px", marginRight: "8px" },
                                children: /* @__PURE__ */ jsx(SearchIcon, { className: "tw:text-primary/60" })
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              "input",
                              {
                                type: "text",
                                placeholder: "Search by city, state or zip code",
                                value: searchInput,
                                onChange: handleSearchInputChange,
                                onKeyDown: handleSearchKeyDown,
                                onFocus: () => {
                                  if (predictions.length > 0) {
                                    setShowPredictions(true);
                                  }
                                },
                                className: "tw:flex-1 tw:border-0 tw:outline-none tw:text-sm tw:font-medium tw:text-primary/50 tw:bg-transparent",
                                style: {
                                  fontFamily: "Poppins, sans-serif",
                                  lineHeight: "1.5",
                                  padding: "0",
                                  margin: "0",
                                  height: "auto",
                                  verticalAlign: "middle"
                                },
                                autoComplete: "off"
                              }
                            )
                          ]
                        }
                      ),
                      showPredictions && predictions.length > 0 && /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: "tw:absolute tw:top-[48px] tw:left-0 tw:w-full tw:bg-white tw:rounded-lg tw:shadow-lg tw:overflow-hidden tw:z-20",
                          style: {
                            border: `1px solid ${getCSSVariable("--color-border", "#DCDDDC")}`,
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: getCSSVariable("--color-background", "#FFFFFF")
                          },
                          children: predictions.map((prediction, index) => /* @__PURE__ */ jsx(
                            "div",
                            {
                              onClick: () => handleSelectPrediction(prediction),
                              className: "tw:px-4 tw:py-2 tw:cursor-pointer tw:text-xs tw:font-medium",
                              style: {
                                fontFamily: "Poppins, sans-serif",
                                color: getCSSVariable("--color-foreground", "#000"),
                                borderBottom: index < predictions.length - 1 ? `1px solid ${getCSSVariable("--color-border", "#DCDDDC")}` : "none",
                                transition: "background-color 0.15s ease"
                              },
                              onMouseEnter: (e) => {
                                e.currentTarget.style.backgroundColor = getCSSVariable("--color-accent", "#F5F5F5");
                              },
                              onMouseLeave: (e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                              },
                              children: prediction.description
                            },
                            prediction.place_id
                          ))
                        }
                      )
                    ]
                  }
                ) }),
                viewState !== "store-details" && /* @__PURE__ */ jsxs(
                  "div",
                  {
                    className: "tw:absolute tw:z-10 tw:w-full",
                    style: {
                      bottom: "0",
                      left: "0",
                      paddingBottom: "12px"
                    },
                    children: [
                      viewState === "store-list" && !loading && /* @__PURE__ */ jsx("div", { className: "tw:mb-3", style: { paddingLeft: "12px", paddingRight: "12px" }, children: /* @__PURE__ */ jsxs(
                        "button",
                        {
                          onClick: handleBackToAllRetailers,
                          className: "tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-2 tw:min-h-[44px] tw:rounded-full tw:bg-white tw:border tw:cursor-pointer tw:transition-colors tw:touch-manipulation",
                          style: {
                            fontFamily: "Poppins, sans-serif",
                            border: `1px solid ${getCSSVariable("--color-border", "#DCDDDC")}`,
                            boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.1, "#000")}`,
                            color: getCSSVariable("--color-foreground", "#000")
                          },
                          onMouseEnter: (e) => {
                            e.currentTarget.style.backgroundColor = getCSSVariable("--color-accent", "#F5F5F5");
                          },
                          onMouseLeave: (e) => {
                            e.currentTarget.style.backgroundColor = "#FFFFFF";
                          },
                          children: [
                            /* @__PURE__ */ jsx(
                              "svg",
                              {
                                width: "16",
                                height: "16",
                                viewBox: "0 0 16 16",
                                fill: "none",
                                xmlns: "http://www.w3.org/2000/svg",
                                style: { flexShrink: 0 },
                                children: /* @__PURE__ */ jsx(
                                  "path",
                                  {
                                    d: "M10 12L6 8L10 4",
                                    stroke: "currentColor",
                                    strokeWidth: "1.5",
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round"
                                  }
                                )
                              }
                            ),
                            /* @__PURE__ */ jsx("span", { className: "tw:text-xs tw:font-semibold tw:tracking-[0.08px]", children: "View all retailers" })
                          ]
                        }
                      ) }),
                      loading ? /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: "tw:flex tw:gap-3 tw:overflow-x-auto tw:scrollbar-hide",
                          style: {
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                            paddingLeft: "12px",
                            paddingRight: "12px"
                          },
                          children: [1, 2, 3].map((i) => /* @__PURE__ */ jsxs(
                            "div",
                            {
                              className: "tw:flex-shrink-0 tw:flex tw:items-start tw:animate-pulse",
                              style: {
                                width: "min(calc(100vw - 80px), 349px)",
                                height: "88px",
                                padding: "12px 16px",
                                gap: "16px",
                                backgroundColor: "#FFFFFF",
                                border: "1px solid #DCDDDC",
                                borderRadius: "8px",
                                boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.08, "#000")}`
                              },
                              children: [
                                /* @__PURE__ */ jsx("div", { className: "tw:w-[33px] tw:h-[33px] tw:rounded-full tw:bg-[#F6F8FA] tw:flex-shrink-0" }),
                                /* @__PURE__ */ jsxs("div", { className: "tw:flex-1 tw:flex tw:flex-col tw:justify-between tw:self-stretch", children: [
                                  /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:items-start tw:justify-between", style: { gap: "8px" }, children: [
                                    /* @__PURE__ */ jsxs("div", { className: "tw:flex tw:flex-col tw:gap-[4px]", children: [
                                      /* @__PURE__ */ jsx("div", { className: "tw:h-[12px] tw:w-[60px] tw:rounded tw:bg-[#F6F8FA]" }),
                                      /* @__PURE__ */ jsx("div", { className: "tw:h-[16px] tw:w-[80px] tw:rounded tw:bg-[#F6F8FA]" })
                                    ] }),
                                    /* @__PURE__ */ jsx("div", { className: "tw:h-[32px] tw:w-[90px] tw:rounded-[16px] tw:bg-[#F6F8FA] tw:flex-shrink-0" })
                                  ] }),
                                  /* @__PURE__ */ jsx("div", { className: "tw:h-[12px] tw:w-[150px] tw:rounded tw:bg-[#F6F8FA]" })
                                ] })
                              ]
                            },
                            i
                          ))
                        }
                      ) : viewState === "retailer-summary" ? /* @__PURE__ */ jsxs(ScrollableCarousel, { scrollRef: carouselRef, paddingX: "12px", children: [
                        retailerSummaries.map((summary) => /* @__PURE__ */ jsx(
                          RetailerSummaryCard,
                          {
                            retailerSummary: summary,
                            onClick: () => handleRetailerSelect(summary.retailerId)
                          },
                          summary.retailerId
                        )),
                        retailerSummaries.length === 0 && /* @__PURE__ */ jsx(
                          "div",
                          {
                            className: "tw:w-full tw:flex tw:items-center tw:justify-center",
                            style: {
                              height: "88px",
                              backgroundColor: "#FFFFFF",
                              border: "1px solid #DCDDDC",
                              borderRadius: "8px",
                              boxShadow: `0 2px 6px ${getColorWithOpacity("--color-foreground", 0.08, "#000")}`
                            },
                            children: /* @__PURE__ */ jsx("p", { className: "tw:text-sm tw:text-[#666]", children: "No stores found nearby" })
                          }
                        )
                      ] }) : /* @__PURE__ */ jsx(ScrollableCarousel, { scrollRef: carouselRef, paddingX: "12px", children: filteredStores.map((store) => /* @__PURE__ */ jsx(
                        StoreCard,
                        {
                          store,
                          onDetailsClick: () => handleStoreDetailsClick(store)
                        },
                        `${store.retailerid}-${store.address}-${store.city}`
                      )) })
                    ]
                  }
                ),
                viewState === "store-details" && selectedStore && /* @__PURE__ */ jsx(
                  StoreDetailPanel,
                  {
                    store: selectedStore,
                    details: storeDetails,
                    loading: detailsLoading,
                    onClose: handleCloseDetails
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
};
const StoreLocatorWidget = ({ stacklineSkus, apiUrl }) => {
  return /* @__PURE__ */ jsx(StoreLocatorWidget$1, { widgetId: "store-locator", displayName: "Store Locator", children: (state, resolveColor) => /* @__PURE__ */ jsx(StoreLocatorStyleContext.Provider, { value: state, children: /* @__PURE__ */ jsx(StoreLocatorWidgetInner, { stacklineSkus, apiUrl, widgetState: state }) }) });
};
export {
  StoreLocatorWidget
};
//# sourceMappingURL=index.js.map
